import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Button,
  Chip,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
} from '@mui/material';
import { EmptyState } from '@/components/ui';
import PageSkeleton from '@/components/PageSkeleton';
import { QueryDocumentSnapshot } from 'firebase/firestore';
import { useActivityHistory } from '@/hooks/useActivityHistory';
import { ActivityHistoryList, EmployeeHistoryItem } from '@/components/ActivityHistory';
import { ActivityHistoryDocument, RestoreDataOptions } from '@/types/activityHistory';
import { EmployeeHistoryData, WorkScheduleHistoryData } from '@/types/employeeHistory';
import { getCurrentUser } from '@/services/authService';
import { loadSharedBookData } from '@/services/firestoreService';
import { useAccountBookStore } from '@/store/accountBookStore';
import { employeeHistoryService } from '@/services/employeeHistoryService';
import SectionHeader from '../../components/SectionHeader';

const ActivityHistoryPage: React.FC = () => {
  const [shareBookId, setShareBookId] = useState<string>('demo-shared-book'); // 데모용 ID
  const [isHost, setIsHost] = useState(false);
  const [currentTab, setCurrentTab] = useState<'expense' | 'employee' | 'schedule'>('expense');
  
  // 가계부 정보
  const { currentAccountBook } = useAccountBookStore();
  
  // 손익 활동 관리
  const {
    histories: expenseHistories,
    stats: expenseStats,
    loading: expenseLoading,
    loadingMore: expenseLoadingMore,
    loadingStats: expenseLoadingStats,
    hasMore: expenseHasMore,
    error: expenseError,
    loadMoreHistories: loadMoreExpenseHistories,
    refresh: refreshExpense,
    restoreData,
    applyFilter: applyExpenseFilter,
    clearFilter: clearExpenseFilter,
    currentFilter: currentExpenseFilter
  } = useActivityHistory({
    shareBookId,
    enableRealtime: true,
    initialLimit: 30
  });

  // accountNumber 결정 로직
  const currentAccountNumber = useMemo(() => {
    const accountNum = currentAccountBook?.accountNumber ?? 1;
    return accountNum;
  }, [currentAccountBook?.accountNumber]);

  // 직원 히스토리 (분리)
  const [employeeHistories, setEmployeeHistories] = useState<EmployeeHistoryData[]>([]);
  const [employeeLoading, setEmployeeLoading] = useState(false);
  const [employeeLoadingMore, setEmployeeLoadingMore] = useState(false);
  const [employeeHasMore, setEmployeeHasMore] = useState(true);
  const [employeeError, setEmployeeError] = useState<string | null>(null);
  const [employeeLastDoc, setEmployeeLastDoc] = useState<QueryDocumentSnapshot | null>(null);

  // 스케줄 히스토리 (분리)
  const [scheduleHistories, setScheduleHistories] = useState<WorkScheduleHistoryData[]>([]);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleLoadingMore, setScheduleLoadingMore] = useState(false);
  const [scheduleHasMore, setScheduleHasMore] = useState(true);
  const [scheduleError, setScheduleError] = useState<string | null>(null);
  const [scheduleLastDoc, setScheduleLastDoc] = useState<QueryDocumentSnapshot | null>(null);

  // 통계
  const [employeeStats, setEmployeeStats] = useState<{ totalCount: number; employeeCounts: Record<string, number> } | null>(null);
  const [scheduleStats, setScheduleStats] = useState<{ totalCount: number; employeeCounts: Record<string, number> } | null>(null);

  // 🔍 앱과 동일한 shareBookId 결정 로직 구현
  const getEffectiveShareBookId = useMemo(() => {
    const currentUser = getCurrentUser();
    if (!currentUser) return null;

    // 🔍 앱과 동일한 로직: 
    // 1. 멤버인 경우: 호스트의 shareBookId 사용
    // 2. 호스트인 경우: 자신의 userId 사용
    // 3. 프리미엄 단독 사용자인 경우: 자신의 userId 사용
    
    // 현재는 임시로 userId 사용 (실제로는 sharedBook 상태에서 currentShareBookId를 가져와야 함)
    // TODO: sharedBook 상태에서 currentShareBookId를 가져오는 로직 구현 필요
    

    return currentUser.uid; // 임시로 userId 사용
  }, [currentAccountBook?.id]);

  // 직원 히스토리 로드
  const loadEmployeeHistory = async (loadMore = false) => {
    try {
      if (loadMore) {
        setEmployeeLoadingMore(true);
      } else {
        setEmployeeLoading(true);
        setEmployeeError(null);
      }

      const result = await employeeHistoryService.getEmployeeHistory(
        shareBookId,
        currentAccountNumber,
        30,
        loadMore ? employeeLastDoc || undefined : undefined
      );

      if (loadMore) {
        setEmployeeHistories(prev => [...prev, ...result.data]);
      } else {
        setEmployeeHistories(result.data);
      }

      setEmployeeLastDoc(result.lastDoc);
      setEmployeeHasMore(result.data.length === 30);
    } catch (error: any) {
      console.error('❌ 직원 히스토리 로드 실패:', error);
      setEmployeeError(error.message || '직원 히스토리를 불러오는데 실패했습니다.');
    } finally {
      setEmployeeLoading(false);
      setEmployeeLoadingMore(false);
    }
  };

  // 스케줄 히스토리 로드
  const loadScheduleHistory = async (loadMore = false) => {
    try {
      if (loadMore) {
        setScheduleLoadingMore(true);
      } else {
        setScheduleLoading(true);
        setScheduleError(null);
      }

      const result = await employeeHistoryService.getWorkScheduleHistory(
        shareBookId,
        currentAccountNumber,
        30,
        loadMore ? scheduleLastDoc || undefined : undefined
      );

      if (loadMore) {
        setScheduleHistories(prev => [...prev, ...result.data]);
      } else {
        setScheduleHistories(result.data);
      }

      setScheduleLastDoc(result.lastDoc);
      setScheduleHasMore(result.data.length === 30);
    } catch (error: any) {
      console.error('❌ 스케줄 히스토리 로드 실패:', error);
      setScheduleError(error.message || '스케줄 히스토리를 불러오는데 실패했습니다.');
    } finally {
      setScheduleLoading(false);
      setScheduleLoadingMore(false);
    }
  };

  // 현재 사용자 확인 및 공유가계부 ID 설정
  useEffect(() => {
    const initializeActivityHistory = async () => {
      try {
        const currentUser = getCurrentUser();
        if (currentUser && getEffectiveShareBookId) {
          
          // 🔍 앱과 동일한 shareBookId 사용
          setShareBookId(getEffectiveShareBookId);
          
          // 공유가계부 데이터 로드 시도
          try {
            const sharedBookData = await loadSharedBookData(getEffectiveShareBookId);
            
            // 호스트 여부 확인
            setIsHost(true); // 현재 사용자가 호스트라고 가정
          } catch (error) {
            console.error('공유가계부 데이터 로드 실패, effectiveShareBookId 사용:', error);
            // 공유가계부가 없어도 effectiveShareBookId로 시도
            setShareBookId(getEffectiveShareBookId);
            setIsHost(true);
          }
        }
      } catch (error) {
        console.error('사용자 상태 확인 실패:', error);
      }
    };

    initializeActivityHistory();
  }, [getEffectiveShareBookId]);

  // 탭 변경 시 데이터 로드
  useEffect(() => {
    if (!shareBookId) return;

    if (currentTab === 'employee' && employeeHistories.length === 0) {
      loadEmployeeHistory();
    } else if (currentTab === 'schedule' && scheduleHistories.length === 0) {
      loadScheduleHistory();
    }
  }, [currentTab, shareBookId, currentAccountNumber]);

  const handleRestore = async (history: ActivityHistoryDocument) => {
    try {
      const restoreOptions: RestoreDataOptions = {
        historyId: history.id,
        shareBookId,
        metadata: history.metadata
      };

      await restoreData(restoreOptions);
      
      // 복원 후 데이터 새로고침
      await refreshExpense();
      
      // 성공 알림 (실제로는 토스트나 스낵바 사용)
    } catch (error) {
      console.error('❌ 데이터 복원 실패:', error);
      throw error;
    }
  };

  const handleFilter = (filter: any) => {
    // 간단한 필터링 (실제로는 더 복잡한 필터링 로직 필요)
    applyExpenseFilter({
      actionTypes: filter.actionType ? [filter.actionType] : undefined,
      userIds: undefined,
      dateRange: undefined,
      restorable: undefined
    });
  };

  const handleClearFilter = () => {
    clearExpenseFilter();
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: 'expense' | 'employee' | 'schedule') => {
    setCurrentTab(newValue);
  };

  const handleEmployeeRefresh = async () => {
    await loadEmployeeHistory(false);
  };

  const handleScheduleRefresh = async () => {
    await loadScheduleHistory(false);
  };

  // 현재 탭에 따른 데이터 선택
  const currentLoading = currentTab === 'expense' ? expenseLoading : 
                        currentTab === 'employee' ? employeeLoading : scheduleLoading;
  const currentLoadingMore = currentTab === 'expense' ? expenseLoadingMore : 
                             currentTab === 'employee' ? employeeLoadingMore : scheduleLoadingMore;
  const currentHasMore = currentTab === 'expense' ? expenseHasMore : 
                         currentTab === 'employee' ? employeeHasMore : scheduleHasMore;
  const currentError = currentTab === 'expense' ? expenseError : 
                       currentTab === 'employee' ? employeeError : scheduleError;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* 페이지 헤더 */}
      <SectionHeader
        size="page"
        eyebrow="기록"
        title="활동 기록"
        subtitle="활동 추적 및 데이터 복원"
      />

      {/* 탭 UI */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            borderBottom: 1,
            borderColor: 'divider'
          }}
        >
          <Tab
            value="expense"
            label="손익 활동 관리"
            sx={{ py: 2 }}
          />
          <Tab
            value="employee"
            label="직원 활동 관리"
            sx={{ py: 2 }}
          />
          <Tab
            value="schedule"
            label="스케줄 활동 관리"
            sx={{ py: 2 }}
          />
        </Tabs>
      </Paper>


      {/* 직원 활동 통계 카드 */}
      {currentTab === 'employee' && employeeStats && employeeStats.totalCount > 0 && (
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={6}>
            <Card sx={{ 
              background: 'var(--bg-base)',
              border: '1px solid var(--border-subtle)',
              borderRadius: 2
            }}>
              <CardContent sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h5" sx={{ fontWeight: 600, color: 'var(--fg-strong)' }}>
                  {employeeStats.totalCount}
                </Typography>
                <Typography variant="body2" sx={{ color: 'var(--fg-secondary)' }}>
                  총 직원 활동 수
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6}>
            <Card sx={{ 
              background: 'var(--bg-base)',
              border: '1px solid var(--border-subtle)',
              borderRadius: 2
            }}>
              <CardContent sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h5" sx={{ fontWeight: 600, color: 'var(--fg-strong)' }}>
                  {Object.keys(employeeStats.employeeCounts).length}
                </Typography>
                <Typography variant="body2" sx={{ color: 'var(--fg-secondary)' }}>
                  직원 수
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* 스케줄 활동 통계 카드 */}
      {currentTab === 'schedule' && scheduleStats && scheduleStats.totalCount > 0 && (
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={6}>
            <Card sx={{ 
              background: 'var(--bg-base)',
              border: '1px solid var(--border-subtle)',
              borderRadius: 2
            }}>
              <CardContent sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h5" sx={{ fontWeight: 600, color: 'var(--fg-strong)' }}>
                  {scheduleStats.totalCount}
                </Typography>
                <Typography variant="body2" sx={{ color: 'var(--fg-secondary)' }}>
                  총 스케줄 활동 수
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6}>
            <Card sx={{ 
              background: 'var(--bg-base)',
              border: '1px solid var(--border-subtle)',
              borderRadius: 2
            }}>
              <CardContent sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h5" sx={{ fontWeight: 600, color: 'var(--fg-strong)' }}>
                  {Object.keys(scheduleStats.employeeCounts).length}
                </Typography>
                <Typography variant="body2" sx={{ color: 'var(--fg-secondary)' }}>
                  직원 수
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* 로딩 상태 */}
      {currentLoading && <PageSkeleton blocks={[72, 72, 72, 72]} />}

      {/* 에러 상태 */}
      {currentError && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 3,
            borderRadius: 2,
            '& .MuiAlert-message': {
              fontSize: '1rem'
            }
          }}
        >
          {currentError}
        </Alert>
      )}

      {/* 손익 활동 기록 목록 */}
      {!currentLoading && currentTab === 'expense' && (
        <ActivityHistoryList
          histories={expenseHistories}
          loading={expenseLoading}
          loadingMore={expenseLoadingMore}
          hasMore={expenseHasMore}
          error={expenseError}
          isHost={isHost}
          onLoadMore={loadMoreExpenseHistories}
          onRefresh={refreshExpense}
          onRestore={handleRestore}
          onFilter={handleFilter}
          onClearFilter={handleClearFilter}
        />
      )}

      {/* 직원 활동 기록 목록 */}
      {!currentLoading && currentTab === 'employee' && (
        <Box>
          {employeeHistories.length === 0 ? (
            <Paper sx={{ background: 'var(--bg-elev-1)', border: '1px solid var(--border-subtle)' }}>
              <EmptyState
                title="직원 활동 기록이 없습니다"
                description="직원 등록, 수정, 삭제 시 활동 기록이 표시됩니다."
              />
            </Paper>
          ) : (
            <>
              {employeeHistories.map((history) => {
                const formatActivityDate = (timestamp: number) => {
                  const date = new Date(timestamp);
                  const now = new Date();
                  const diffMs = now.getTime() - date.getTime();
                  const diffMinutes = Math.floor(diffMs / (1000 * 60));
                  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
                  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

                  if (diffMinutes < 1) return '방금 전';
                  if (diffMinutes < 60) return `${diffMinutes}분 전`;
                  if (diffHours < 24) return `${diffHours}시간 전`;
                  if (diffDays < 7) return `${diffDays}일 전`;
                  
                  const year = date.getFullYear().toString().substring(2);
                  const month = (date.getMonth() + 1).toString().padStart(2, '0');
                  const day = date.getDate().toString().padStart(2, '0');
                  return `${year}/${month}/${day}`;
                };

                return (
                  <Card 
                    key={history.id}
                    sx={{ 
                      mb: 2, 
                      background: 'var(--bg-base)',
                      border: '1px solid var(--border-subtle)',
                      borderRadius: 2,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        boxShadow: 'var(--shadow-1)',
                      }
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        {/* 아이콘 */}
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            background: 'var(--bg-elev-1)',
                            border: '1px solid var(--border-subtle)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '1.2rem',
                            color: 'var(--fg-secondary)',
                            flexShrink: 0
                          }}
                        >
                          {history.action === 'CREATE' ? '➕' :
                           history.action === 'UPDATE' ? '✏️' :
                           '🗑️'}
                        </Box>

                        {/* 콘텐츠 */}
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          {/* 설명 */}
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              fontWeight: 500, 
                              color: 'var(--fg-strong)',
                              mb: 1,
                              lineHeight: 1.4
                            }}
                          >
                            {history.description || `직원 "${history.employeeName}"을 ${
                              history.action === 'CREATE' ? '추가' :
                              history.action === 'UPDATE' ? '수정' :
                              '삭제'
                            }했습니다`}
                          </Typography>

                          {/* 간단한 메타 정보 */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                            <Typography variant="caption" sx={{ color: 'var(--fg-secondary)' }}>
                              플러터블배우자 • {formatActivityDate(history.timestamp)}
                            </Typography>
                          </Box>

                          {/* 액션 타입 칩 */}
                          <Chip
                            label={
                              history.action === 'CREATE' ? '직원 추가' :
                              history.action === 'UPDATE' ? '직원 수정' :
                              '직원 삭제'
                            }
                            size="small"
                            variant="outlined"
                            sx={{
                              fontSize: '0.7rem',
                              fontWeight: 500,
                              borderColor: 'var(--border-subtle)',
                              color: 'var(--fg-secondary)',
                              '& .MuiChip-label': { px: 1.5 },
                            }}
                          />
                        </Box>

                        {/* 액션 버튼들 */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {history.action === 'DELETE' && isHost && (
                            <Button
                              variant="text"
                              size="small"
                              onClick={() => {
                                // TODO: 직원 복원 로직 구현
                                alert(`직원 "${history.employeeName}" 복원 기능은 곧 추가됩니다.`);
                              }}
                            >
                              복원
                            </Button>
                          )}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
              
              {/* 더 불러오기 */}
              {employeeHasMore && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  {employeeLoadingMore ? (
                    <CircularProgress size={36} />
                  ) : (
                    <Button
                      variant="outlined"
                      onClick={() => loadEmployeeHistory(true)}
                      sx={{ minWidth: 200 }}
                    >
                      더 불러오기
                    </Button>
                  )}
                </Box>
              )}
              
              {/* 모든 데이터 로드 완료 */}
              {!employeeHasMore && employeeHistories.length > 0 && (
                <Typography 
                  variant="body2" 
                  sx={{ 
                    textAlign: 'center', 
                    color: 'var(--fg-disabled)', 
                    py: 3,
                    borderTop: '1px solid var(--border-subtle)',
                    mt: 2
                  }}
                >
                  모든 활동 기록을 불러왔습니다
                </Typography>
              )}
            </>
          )}
        </Box>
      )}

      {/* 스케줄 활동 기록 목록 */}
      {!currentLoading && currentTab === 'schedule' && (
        <Box>
          {scheduleHistories.length === 0 ? (
            <Paper sx={{ background: 'var(--bg-elev-1)', border: '1px solid var(--border-subtle)' }}>
              <EmptyState
                title="스케줄 활동 기록이 없습니다"
                description="근무 일정 등록, 수정, 삭제 시 활동 기록이 표시됩니다."
              />
            </Paper>
          ) : (
            <>
              {scheduleHistories.map((history) => {
                const formatActivityDate = (timestamp: number) => {
                  const date = new Date(timestamp);
                  const now = new Date();
                  const diffMs = now.getTime() - date.getTime();
                  const diffMinutes = Math.floor(diffMs / (1000 * 60));
                  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
                  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

                  if (diffMinutes < 1) return '방금 전';
                  if (diffMinutes < 60) return `${diffMinutes}분 전`;
                  if (diffHours < 24) return `${diffHours}시간 전`;
                  if (diffDays < 7) return `${diffDays}일 전`;
                  
                  const year = date.getFullYear().toString().substring(2);
                  const month = (date.getMonth() + 1).toString().padStart(2, '0');
                  const day = date.getDate().toString().padStart(2, '0');
                  return `${year}/${month}/${day}`;
                };

                return (
                  <Card 
                    key={history.id}
                    sx={{ 
                      mb: 2, 
                      background: 'var(--bg-base)',
                      border: '1px solid var(--border-subtle)',
                      borderRadius: 2,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        boxShadow: 'var(--shadow-1)',
                      }
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        {/* 아이콘 */}
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            background: 'var(--bg-elev-1)',
                            border: '1px solid var(--border-subtle)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '1.2rem',
                            color: 'var(--fg-secondary)',
                            flexShrink: 0
                          }}
                        >
                          {history.action === 'CREATE' ? '➕' :
                           history.action === 'UPDATE' ? '✏️' :
                           '🗑️'}
                        </Box>

                        {/* 콘텐츠 */}
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          {/* 설명 */}
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              fontWeight: 500, 
                              color: 'var(--fg-strong)',
                              mb: 1,
                              lineHeight: 1.4
                            }}
                          >
                            {history.description || `"${history.employeeName}"의 근무 일정을 ${
                              history.action === 'CREATE' ? '추가' :
                              history.action === 'UPDATE' ? '수정' :
                              '삭제'
                            }했습니다 (${history.workDate})`}
                          </Typography>

                          {/* 간단한 메타 정보 */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                            <Typography variant="caption" sx={{ color: 'var(--fg-secondary)' }}>
                              플러터블배우자 • {formatActivityDate(history.timestamp)}
                            </Typography>
                            {history.startTime && history.endTime && (
                              <Typography variant="caption" sx={{ color: 'var(--fg-secondary)', fontWeight: 500 }}>
                                근무시간: {history.startTime} ~ {history.endTime}
                              </Typography>
                            )}
                          </Box>

                          {/* 액션 타입 칩 */}
                          <Chip
                            label={
                              history.action === 'CREATE' ? '스케줄 추가' :
                              history.action === 'UPDATE' ? '스케줄 수정' :
                              '스케줄 삭제'
                            }
                            size="small"
                            variant="outlined"
                            sx={{
                              fontSize: '0.7rem',
                              fontWeight: 500,
                              borderColor: 'var(--border-subtle)',
                              color: 'var(--fg-secondary)',
                              '& .MuiChip-label': { px: 1.5 },
                            }}
                          />
                        </Box>

                        {/* 액션 버튼들 */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {history.action === 'DELETE' && isHost && (
                            <Button
                              variant="text"
                              size="small"
                              onClick={() => {
                                // TODO: 스케줄 복원 로직 구현
                                alert(`"${history.employeeName}"의 ${history.workDate} 스케줄 복원 기능은 곧 추가됩니다.`);
                              }}
                            >
                              복원
                            </Button>
                          )}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
              
              {/* 더 불러오기 */}
              {scheduleHasMore && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  {scheduleLoadingMore ? (
                    <CircularProgress size={36} />
                  ) : (
                    <Button
                      variant="outlined"
                      onClick={() => loadScheduleHistory(true)}
                      sx={{ minWidth: 200 }}
                    >
                      더 불러오기
                    </Button>
                  )}
                </Box>
              )}
              
              {/* 모든 데이터 로드 완료 */}
              {!scheduleHasMore && scheduleHistories.length > 0 && (
                <Typography 
                  variant="body2" 
                  sx={{ 
                    textAlign: 'center', 
                    color: 'var(--fg-disabled)', 
                    py: 3,
                    borderTop: '1px solid var(--border-subtle)',
                    mt: 2
                  }}
                >
                  모든 활동 기록을 불러왔습니다
                </Typography>
              )}
            </>
          )}
        </Box>
      )}

      {/* 권한 안내 */}
      {!isHost && (
        <Paper sx={{ 
          p: 3, 
          mt: 4, 
          background: 'var(--bg-elev-1)',
          border: '1px solid var(--border-subtle)'
        }}>
          <Typography variant="body2" sx={{ color: 'var(--fg-secondary)' }}>
            <strong>알림:</strong> 현재 멤버 권한으로 활동 기록을 조회할 수 있습니다. 
            데이터 복원 기능은 호스트만 사용할 수 있습니다.
          </Typography>
        </Paper>
      )}
    </Container>
  );
};

export default ActivityHistoryPage;
